// Web replacement for @react-native-firebase/auth on top of the Firebase JS SDK.
// Only the surface the app uses: currentUser, onAuthStateChanged, signInWithCustomToken, signOut.
import {
  browserLocalPersistence,
  indexedDBLocalPersistence,
  initializeAuth,
  onAuthStateChanged,
  signInWithCustomToken,
  signOut,
  type User,
  type UserCredential,
} from 'firebase/auth';
import { firebaseApp } from '../firebase';

// initializeAuth instead of getAuth: without a popupRedirectResolver the SDK does not load
// apis.google.com scripts, which the CSP blocks and the app (custom-token sign-in only) never needs.
const webAuth = initializeAuth(firebaseApp, {
  persistence: [indexedDBLocalPersistence, browserLocalPersistence],
});

// The JS SDK restores the persisted user asynchronously, while the app reads `currentUser`
// synchronously at startup (as it can with the native SDK). web/index.tsx waits for this first.
export const authReady: Promise<void> = webAuth.authStateReady();

export namespace FirebaseAuthTypes {
  export type User = import('firebase/auth').User;
  export type UserCredential = import('firebase/auth').UserCredential;
}

const module = {
  get currentUser(): User | null {
    return webAuth.currentUser;
  },
  onAuthStateChanged(listener: (user: User | null) => void) {
    return onAuthStateChanged(webAuth, listener);
  },
  signInWithCustomToken(token: string): Promise<UserCredential> {
    return signInWithCustomToken(webAuth, token);
  },
  signOut() {
    return signOut(webAuth);
  },
};

export default function auth() {
  return module;
}
